import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { ProductCard } from "./ProductCard";
import { Search, SlidersHorizontal, X } from "lucide-react";

const SORTS = [["featured", "Featured"], ["newest", "Newest"], ["price_asc", "Price: Low → High"], ["price_desc", "Price: High → Low"]];

export default function MarketplaceProducts() {
  const [params, setParams] = useSearchParams();
  const [collections, setCollections] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");
  const collection = params.get("collection") || "";
  const product_type = params.get("type") || "";
  const availability = params.get("availability") || "";
  const sort = params.get("sort") || "featured";

  useEffect(() => { api.get("/marketplace/collections").then(({ data }) => setCollections(data.items)).catch(() => {}); }, []);

  const load = useCallback(async (pg, append) => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketplace/products", { params: { q: q || undefined, collection: collection || undefined, product_type: product_type || undefined, availability: availability || undefined, sort, page: pg, page_size: 12 } });
      setItems((prev) => append ? [...prev, ...data.items] : data.items);
      setTotal(data.total); setPages(data.pages); setPage(data.page);
    } finally { setLoading(false); }
  }, [q, collection, product_type, availability, sort]);

  useEffect(() => { const t = setTimeout(() => load(1, false), 250); return () => clearTimeout(t); }, [load]);

  const setParam = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); setParams(n); };

  const Filters = () => (
    <div className="space-y-5">
      <div>
        <div className="label-overline text-[10px] mb-2">Collection</div>
        <div className="space-y-1">
          <button onClick={() => setParam("collection", "")} className={`block text-sm ${!collection ? "text-[#E63946] font-semibold" : "text-slate-600"}`}>All</button>
          {collections.map((c) => <button key={c.id} data-testid={`filter-collection-${c.slug}`} onClick={() => setParam("collection", c.slug)} className={`block text-sm ${collection === c.slug ? "text-[#E63946] font-semibold" : "text-slate-600 hover:text-[#0A192F]"}`}>{c.name}</button>)}
        </div>
      </div>
      <div>
        <div className="label-overline text-[10px] mb-2">Availability</div>
        {[["", "All"], ["available", "In stock"], ["preorder", "Preorder"], ["sold_out", "Sold out"]].map(([v, l]) => (
          <button key={v} onClick={() => setParam("availability", v)} className={`block text-sm ${availability === v ? "text-[#E63946] font-semibold" : "text-slate-600 hover:text-[#0A192F]"}`}>{l}</button>
        ))}
      </div>
    </div>
  );

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="marketplace-products">
        <h1 className="font-display text-3xl uppercase tracking-tight">Shop All</h1>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center border border-slate-300 rounded-sm bg-white flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 ml-2" />
            <input data-testid="products-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="flex-1 px-2 py-2 text-sm outline-none" />
          </div>
          <select data-testid="products-sort" value={sort} onChange={(e) => setParam("sort", e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm bg-white">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button data-testid="filters-toggle" onClick={() => setDrawer(true)} className="lg:hidden inline-flex items-center gap-1 border border-slate-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><SlidersHorizontal className="w-4 h-4" /> Filters</button>
        </div>

        <div className="mt-6 flex gap-8">
          <aside className="hidden lg:block w-52 shrink-0"><Filters /></aside>
          <div className="flex-1 min-w-0">
            {loading && items.length === 0 ? <div className="text-slate-400 py-16 text-center">Loading…</div>
              : items.length === 0 ? <div data-testid="no-results" className="text-slate-400 py-16 text-center">No products match your search.</div>
              : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{items.map((p) => <ProductCard key={p.id} p={p} />)}</div>
                  {page < pages && <div className="text-center mt-8"><button data-testid="load-more" onClick={() => load(page + 1, true)} className="border border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-6 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm transition-colors">Load More ({total - items.length} more)</button></div>}
                </>
              )}
          </div>
        </div>
      </div>

      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setDrawer(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white p-5 overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="filters-drawer">
            <div className="flex items-center justify-between mb-4"><span className="font-display uppercase tracking-tight">Filters</span><button onClick={() => setDrawer(false)}><X className="w-5 h-5" /></button></div>
            <Filters />
          </div>
        </div>
      )}
    </PublicLayout>
  );
}
